import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Award, ShieldCheck, Star, ArrowRight, Hammer, Home, Sun } from "lucide-react";
import { SEO, breadcrumbSchema } from "@/components/SEO";
import { SectionHeading, FinalCTA } from "@/components/Shared";
import { DarkHeroAccent, RoofAccent } from "@/components/RoofAccents";
import { BUSINESS, IMAGES } from "@/data/content";

const SITE = "https://roofing-goral.com";

const AWARDS = [
  { t: "Master Shingle Applicator (Wizard Level)", d: "The highest residential install credential issued by CertainTeed - awarded only after thousands of verified installs and a written exam. Less than 1% of US roofers hold it." },
  { t: "ShingleMaster Credential", d: "Manufacturer-trained on the complete CertainTeed shingle system, including underlayment, flashing, and ventilation - which extends manufacturer warranty terms on every install we register." },
  { t: "VELUX Certified Skylight Installer", d: "Authorized to install and warranty-register VELUX skylights, including curb-mount, deck-mount, and Sun Tunnel daylighting systems. Critical for any leak-free skylight in Michigan winters." },
  { t: "Licensed - State of Michigan", d: "Residential Builder license in good standing, fully insured (general liability + worker's comp). License number provided in writing on every contract." },
];

const KNOWS_ABOUT = [
  { i: Home, t: "Roof Replacement", d: "Full tear-off and re-roof on single-family homes from 1,500 to 6,000 sq ft. Asphalt, metal, tile and slate systems - all flashed and vented to manufacturer spec." },
  { i: ShieldCheck, t: "Insurance Claims", d: "Twenty plus years working with State Farm, Allstate, Auto-Owners, AAA, Citizens, Farm Bureau and the major Michigan carriers. On-site adjuster meetings, supplements, and code-upgrade documentation." },
  { i: Hammer, t: "Storm Damage Restoration", d: "Hail bruise documentation, wind-lift assessment, emergency tarping, fallen-tree impact, and full restoration with code-current materials and ice & water shield to six feet." },
  { i: Sun, t: "Skylight Installation", d: "VELUX-certified flashing kits, curb fabrication for low-slope retrofits, and full integration with surrounding ice & water shield. We warranty the install for ten years on top of VELUX's twenty." },
];

export default function MaciejPage() {
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE}/about/maciej-ogonowski#person`,
    name: "Maciej Ogonowski",
    givenName: "Maciej",
    familyName: "Ogonowski",
    alternateName: ["Maciek Ogonowski", "Maciej Goral"],
    jobTitle: "Owner & Master Shingle Applicator (Wizard Level)",
    description: "Owner of Goral Construction LLC in Waterford, Michigan. Master Shingle Applicator (Wizard Level), ShingleMaster, and VELUX Certified. 28+ years of Michigan roofing experience since 1998.",
    image: `${SITE}${IMAGES.team}`,
    url: `${SITE}/about/maciej-ogonowski`,
    worksFor: { "@id": `${SITE}/#business` },
    knowsLanguage: ["en", "pl"],
    knowsAbout: ["Residential Roofing", "Asphalt Shingle Installation", "Metal Roofing", "Insurance Claims", "Storm Damage Restoration", "Skylight Installation", "Ice Dam Prevention", "Attic Ventilation"],
    award: ["Master Shingle Applicator (Wizard Level)", "ShingleMaster Credential", "VELUX Certified Skylight Installer"],
    hasOccupation: {
      "@type": "Occupation",
      name: "Roofing Contractor",
      occupationLocation: { "@type": "AdministrativeArea", name: "Oakland County, Michigan" },
      skills: "Roof replacement, storm damage assessment, insurance claim coordination, attic ventilation design, ice dam prevention",
    },
    sameAs: [
      BUSINESS.social.angi,
      BUSINESS.social.facebook,
      BUSINESS.social.googleBusiness,
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.address.street,
      addressLocality: BUSINESS.address.city,
      addressRegion: BUSINESS.address.state,
      postalCode: BUSINESS.address.zip,
      addressCountry: "US",
    },
  };

  return (
    <div>
      <SEO
        title="Maciej Ogonowski - Owner, Goral Construction LLC | Oakland County, MI Roofer"
        description="Meet Maciej Ogonowski, owner of Goral Construction LLC. Master Shingle Applicator (Wizard Level), ShingleMaster, VELUX Certified. 28+ years on Michigan roofs since 1998."
        path="/about/maciej-ogonowski"
        image={IMAGES.team}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            personSchema,
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "About", path: "/about" },
              { name: "Maciej Ogonowski", path: "/about/maciej-ogonowski" },
            ]),
            {
              "@context": "https://schema.org",
              "@type": "ProfilePage",
              "@id": `${SITE}/about/maciej-ogonowski#profile`,
              mainEntity: { "@id": `${SITE}/about/maciej-ogonowski#person` },
              about: { "@id": `${SITE}/#business` },
              name: "Maciej Ogonowski - Owner, Goral Construction LLC",
              dateModified: new Date().toISOString().slice(0, 10),
            },
          ],
        }}
      />

      {/* HERO */}
      <section className="bg-bg-dark text-white py-20 md:py-28 relative overflow-hidden">
        <DarkHeroAccent tone="house" />
        <div className="container-x relative grid md:grid-cols-[1fr_auto] gap-12 items-center">
          <div>
            <div className="overline text-brand">Owner Profile · Oakland County, MI</div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
              Maciej Ogonowski - <span className="text-brand">Owner, Goral Construction LLC.</span>
            </h1>
            <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
              Master Shingle Applicator (Wizard Level). ShingleMaster. VELUX Certified. 28+ years on Michigan roofs since 1998. Family-owned, owner-installed, neighbor-to-neighbor.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary" data-testid="maciej-call-cta"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
              <Link to="/get-estimate" className="btn-inverted" data-testid="maciej-estimate-cta">Request a free estimate <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="mt-8 flex items-center gap-1 text-sm text-white/70">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-brand text-brand" />)}
              <span className="ml-2">5.0 on Angi · {BUSINESS.rating.count} verified reviews</span>
            </div>
          </div>
          <div className="hidden md:block w-72 aspect-[3/4] overflow-hidden border-2 border-brand/30">
            <img src={IMAGES.team} alt="Maciej Ogonowski, owner of Goral Construction LLC, on a finished roof in Waterford, MI" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* INTRO + JOURNEY */}
      <section className="py-16 md:py-24">
        <div className="container-x grid md:grid-cols-3 gap-12">
          <div>
            <SectionHeading overline="My journey" title="From one ladder to Oakland County's family-owned roofing crew." />
            <div className="mt-6 aspect-square overflow-hidden bg-bg-soft md:hidden">
              <img src={IMAGES.team} alt="Maciej Ogonowski on a Michigan roof" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-2 space-y-5 text-lg text-ink leading-[1.75]" id="my-journey">
            <p>
              <span className="first-letter:font-heading first-letter:text-7xl first-letter:font-extrabold first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.85] first-letter:text-brand">My</span>
              first day on a Michigan roof was in 1998. I was a young installer with a hammer, a chalk line, and a stubborn belief that the job was either done right or it was not done at all. Twenty-eight years later that belief has not changed. What has changed is how much I know about why roofs fail in Oakland County winters, how to spot a wind-lifted shingle from the driveway, and how to write a claim scope an adjuster will actually approve.
            </p>
            <p>
              I spent the first decade of my career installing for other crews across Waterford, Clarkston, Bloomfield Hills, Troy, and the rest of the county. I learned every shortcut other contractors take. I also learned which of those shortcuts cause leaks at year three and which last twenty years. That experience is why <strong>Goral Construction LLC was founded in 2010</strong> as a family business with one rule: do the job the way I would do it on my mother's house.
            </p>
            <p>
              Since starting the LLC, our crew has installed and repaired well over a thousand roofs across Oakland County. We never subcontract. The same faces show up every job, every season, in every weather. When you hire Goral Construction you are not hiring a sales team with a phone bank - you are hiring a neighbor with 28 years on Michigan roofs and a crew that lives in the towns where they work.
            </p>
          </div>
        </div>
      </section>

      {/* EXPERTISE & CERTIFICATIONS */}
      <section className="py-16 md:py-20 bg-bg-soft border-y border-ink-line" id="certifications">
        <div className="container-x">
          <SectionHeading overline="Expertise & certifications" title="The credentials behind every install." />
          <p className="text-ink-soft mt-4 max-w-3xl text-lg leading-relaxed">
            Manufacturer certifications are not marketing labels - they are factory-trained credentials that extend warranty terms on the products we install. Here is what they actually mean for the roof on your house.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {AWARDS.map((a) => (
              <div key={a.t} className="bg-white border border-ink-line p-6 relative overflow-hidden">
                <RoofAccent variant="gable" position="top-right" size="sm" intensity="soft" />
                <div className="flex items-start gap-3 relative">
                  <Award className="w-6 h-6 text-brand flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading text-lg font-extrabold">{a.t}</h3>
                    <p className="text-ink-soft mt-2 leading-relaxed text-[15px]">{a.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-16 md:py-24" id="philosophy">
        <div className="container-x grid md:grid-cols-3 gap-12">
          <div>
            <SectionHeading overline="Philosophy" title="Done right the first time." />
          </div>
          <div className="md:col-span-2 space-y-5 text-lg text-ink leading-[1.75]">
            <p>
              I built Goral Construction around three rules that have not changed in fifteen years. <strong>First, the same crew on every job.</strong> No subcontractors. The installer who tears off your roof is the same person who flashes your chimney, the same person who sweeps your driveway with the magnet, and the same person whose phone number is on the warranty card. Accountability lives or dies on continuity.
            </p>
            <p>
              <strong>Second, the materials we install are the materials we would put on our own homes.</strong> Architectural shingles, never 3-tab. Ice & water shield six feet from the eave, never the two-foot minimum. Synthetic underlayment, never felt. Step flashing properly woven into the siding, never just caulked. The difference adds a few hundred dollars to an average install and decades to its life.
            </p>
            <p>
              <strong>Third, the honest answer always comes first.</strong> If your roof has five good years left, we tell you that and walk away. If a repair makes more sense than replacement, we write up the repair. If your insurance scope is short on flashing or code upgrades, we file the supplement before we ask for the contract. The customers who hire us once hire us for life because of that. The customers who walk to a cheaper quote sometimes call us six months later, and we go fix what the lowest bidder did wrong.
            </p>
          </div>
        </div>
      </section>

      {/* KNOWS ABOUT - expanded schema */}
      <section className="py-16 md:py-20 bg-bg-soft border-y border-ink-line" id="expertise">
        <div className="container-x">
          <SectionHeading overline="Beyond shingles" title="Where deep experience actually matters." />
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {KNOWS_ABOUT.map((k) => (
              <div key={k.t} className="bg-white border border-ink-line p-6 flex items-start gap-4">
                <k.i className="w-7 h-7 text-brand flex-shrink-0" />
                <div>
                  <h3 className="font-heading text-lg font-extrabold">{k.t}</h3>
                  <p className="text-ink-soft mt-2 leading-relaxed text-[15px]">{k.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section className="py-16 md:py-20">
        <div className="container-x max-w-3xl text-center">
          <SectionHeading overline="Call Maciej directly" title="Same number, same person, since 2010." align="center" />
          <p className="text-ink-soft mt-4 max-w-xl mx-auto leading-relaxed text-lg">
            The phone rings on my hip - not a call center. Day or night, weekend, holiday, emergency leak. If you want a real estimate from the owner of the company, call the number on every truck.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary text-base"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
            <a href={`mailto:${BUSINESS.email}`} className="btn-secondary"><Mail className="w-4 h-4" /> Email me</a>
            <Link to="/get-estimate" className="btn-secondary">Request a free estimate <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm text-ink-soft">
            <div><strong className="block text-ink font-heading">Office</strong>{BUSINESS.address.full}</div>
            <div><strong className="block text-ink font-heading">Hours</strong>{BUSINESS.hours.weekdays}<br /><span className="text-brand font-semibold">{BUSINESS.hours.emergency}</span></div>
            <div><strong className="block text-ink font-heading">Languages</strong>English + Polish</div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
